import React from 'react';
import PropTypes from 'prop-types';

import {
  Grid,
  Popper,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  DialogActions,
  Button,
  Popover,
} from 'views/components';

import {ContentCopy as ContentCopyIcon, Edit as EditIcon, Delete as DeleteIcon, InsertEmoticon} from 'views/icons';

import {Zoom} from 'views/components';

import {translate, Translate} from 'views/module';

import EmojiPicker from './emoji/EmojiPicker';


const DeleteConfirmDialog = (props) => {
  const {
    setDialogOpen,
    onClickConfirmDelete,
  } = props;

  const closeModal = () => {
    setDialogOpen(false);
  };

  return (
    <Dialog onClose={closeModal}>
      <DialogTitle onClose={closeModal}>
        <Translate expression='generic.ConfirmTheAction'/>
      </DialogTitle>
      <DialogContent>
        <Typography>
          <Translate expression='generic.DeleteConfirm'/>
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={closeModal}>
          <Translate expression='generic.Cancel'/>
        </Button>
        <Button onClick={onClickConfirmDelete}>
          <Translate expression='generic.Confirm'/>
        </Button>
      </DialogActions>
    </Dialog>
  );
};
DeleteConfirmDialog.propTypes = {
  setDialogOpen: PropTypes.func.isRequired,
  onClickConfirmDelete: PropTypes.func.isRequired,
};

const HoverToolBox = (props) => {
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [openEmojiPicker, setOpenEmojiPicker] = React.useState(false);

  const {
    toolBar, // Toolbar visible or not visible
    anchorEl, // Element that toolbar hovers over
    copiedText, // Text of ticket comment that toolBox is currently hovering over
    onClickEdit, // function to handle edit button clicked
    onClickDelete, // function to handle delete button clicked
    onEmojiSelected,
    marginTop=8,
    hideTool,
  } = props;

  // Change color of copy button when clicked
  const [copyButton, setCopyButton] = React.useState(false);

  const emojiPickerAnchorRef = React.useRef();

  // Copy ticket comment to clipboard
  const copyComment = () => {
    setCopyButton(true);
    return window.navigator.clipboard.writeText(copiedText);
  };

  const onClickDeleteWrapper = () => {
    setDialogOpen(true);
  };

  const onClickConfirmDelete = () => {
    onClickDelete(setIsDeleting);
    setDialogOpen(false);
  };

  const onEmojiSelect = (selectedEmoji) => {
    if (onEmojiSelected) {
      onEmojiSelected(selectedEmoji.native);
    }
    setOpenEmojiPicker(false);
  };

  const toolElements = {
    emoji: <Grid key='emoji' item>
      <Tooltip title={translate('correspondances.ToolBar.Emoji')} placement="top"
        TransitionComponent={Zoom}
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -10],
              },
            },
          ],
        }}>
        <IconButton ref={emojiPickerAnchorRef} onClick={() => setOpenEmojiPicker((prev) => !prev)} data-testid='emojiButton'>
          <InsertEmoticon/>
        </IconButton>
      </Tooltip>
    </Grid>,
    copy: <Grid key='copy' item>
      <Tooltip title={translate('correspondances.ToolBar.Copy')} placement="top"
        TransitionComponent={Zoom}
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -10],
              },
            },
          ],
        }}>
        <IconButton onClick={copyComment} data-id='copyButton'>
          <ContentCopyIcon sx={{color: copyButton && 'secondary.main'}}/>
        </IconButton>
      </Tooltip>
    </Grid>,
    edit: <Grid key='edit' item>
      <Tooltip title={translate('correspondances.ToolBar.Edit')} placement="top"
        TransitionComponent={Zoom}
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -10],
              },
            },
          ],
        }}
      >
        <IconButton data-testid='editButton' onClick={onClickEdit}>
          <EditIcon/>
        </IconButton>
      </Tooltip>
    </Grid>,
    delete: <Grid key='delete' item>
      <Tooltip title={translate('correspondances.ToolBar.Delete')} placement="top"
        TransitionComponent={Zoom}
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -10],
              },
            },
          ],
        }}
      >
        <span>
          <IconButton data-testid='deleteButton' disabled={isDeleting} onClick={onClickDeleteWrapper}>
            {
          isDeleting ?
            <CircularProgress size='1rem'/> :
            <DeleteIcon/>
            }
          </IconButton>
        </span>
      </Tooltip>
    </Grid>,
  };

  // Reset color of tool bar buttons when mouse leaves comment/announcement
  React.useEffect(() => {
    setCopyButton(false);
  }, [anchorEl, toolBar]);

  const calculateToolbarOffset = React.useCallback(() => {
    const totalNumTools = Object.values(toolElements).length;
    const sizeOfButton = 40;
    const marginRight = 8;
    if (!hideTool) {
      return (totalNumTools * -sizeOfButton) - marginRight;
    }
    const numhiddenTools = Object.values(hideTool).filter(Boolean).length;
    return (totalNumTools * -sizeOfButton) + (numhiddenTools * sizeOfButton) - marginRight;
  }, [hideTool]);

  return (
    <>
      <Popper
        open={toolBar}
        onClickAway={() => {}} // Custom popper requires clickAway function but not applicable here
        anchorEl={anchorEl}
        placement='right-start'
        popperModifiers={[ // Position popper to appear in top right corner of ticket comment
          {
            name: 'offset',
            options: {
              offset: [marginTop, calculateToolbarOffset()],
            },
          },
        ]}
      >
        <Grid container>
          {
            Object.keys(toolElements).map((tool) => {
              if (hideTool[tool]) {
                return;
              }
              return toolElements[tool];
            })
          }
        </Grid>
      </Popper>
      <Popover
        open={openEmojiPicker}
        anchorEl={emojiPickerAnchorRef.current}
        onClose={() => setOpenEmojiPicker(false)}
        disableRestoreFocus // helpful to prevent scroll jumping to top when closing popover
      >
        <EmojiPicker
          onEmojiSelect={onEmojiSelect}
        />
      </Popover>
      {
        dialogOpen &&
        <DeleteConfirmDialog setDialogOpen={setDialogOpen} onClickConfirmDelete={onClickConfirmDelete}/>
      }
    </>
  );
};

HoverToolBox.propTypes = {
  toolBar: PropTypes.bool,
  anchorEl: PropTypes.object,
  copiedText: PropTypes.string,
  onClickEdit: PropTypes.func,
  onClickDelete: PropTypes.func,
  onEmojiSelected: PropTypes.func,
  marginTop: PropTypes.number,
  hideTool: PropTypes.shape({
    emoji: PropTypes.bool,
    copy: PropTypes.bool,
    edit: PropTypes.bool,
    delete: PropTypes.bool,
  }),
};

export default React.memo(HoverToolBox);
